import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Progress, Typography } from 'antd';
import { CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TPuzzleLessonConfig, createEmptyPuzzleLessonConfig } from '../../../models/courses/puzzleLesson';
import { useUserApi } from '../../../api/user/userApp';
import './PuzzleLessonStudent.css';

const { Text } = Typography;
const { TextArea } = Input;

const normalize = (value: string) => value.trim().toLowerCase();

const computeFoundKeywords = (prompt: string, keywords: string[]) => {
  if (!keywords.length) return new Set<string>();
  const lower = normalize(prompt);
  const found = new Set<string>();
  keywords.forEach((kw) => {
    if (!kw) return;
    const norm = normalize(kw);
    if (lower.includes(norm)) found.add(norm);
  });
  return found;
};

interface PuzzleLessonStudentProps {
  config: TPuzzleLessonConfig;
  statement?: string;
  onComplete?: () => void;
  forceReveal?: boolean;
  lessonId?: number;
}

const PuzzleLessonStudent: React.FC<PuzzleLessonStudentProps> = ({
  config,
  statement,
  onComplete,
  forceReveal = false,
  lessonId,
}) => {
  const { t } = useTranslation();
  const safeConfig = useMemo(
    () => ({ ...createEmptyPuzzleLessonConfig(), ...config, components: config.components || [] }),
    [config],
  );
  const [prompt, setPrompt] = useState('');
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const completedRef = useRef(false);
  const hasSavedRef = useRef(false);
  const { getUserAnswerById, saveUserAnswerById } = useUserApi();

  // Restore previous work
  useEffect(() => {
    hasSavedRef.current = false;
    completedRef.current = false;
    if (!lessonId) return;
    getUserAnswerById(lessonId)
      .then((resp: any) => {
        const workspace: string | undefined = resp?.data?.workspace;
        if (!workspace) return;
        try {
          const parsed = JSON.parse(workspace);
          if (parsed && typeof parsed === 'object') {
            if (typeof parsed.prompt === 'string') setPrompt(parsed.prompt);
            if (typeof parsed.submittedPrompt === 'string') {
              setSubmittedPrompt(parsed.submittedPrompt);
              setSubmitted(Boolean(parsed.submitted));
            }
          }
        } catch (_) {
          // ignore invalid workspace
        }
      })
      .catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const effectivePrompt = useMemo(
    () => (forceReveal ? prompt : submittedPrompt),
    [forceReveal, prompt, submittedPrompt],
  );

  const allKeywords = useMemo(() => safeConfig.components.map((c) => c.keyword).filter(Boolean), [safeConfig.components]);
  const found = useMemo(() => computeFoundKeywords(effectivePrompt, allKeywords), [effectivePrompt, allKeywords]);

  const requiredMatches = useMemo(() => {
    if (!safeConfig.requiredMatches || safeConfig.requiredMatches <= 0) {
      return safeConfig.components.length || 0;
    }
    return Math.min(safeConfig.requiredMatches, safeConfig.components.length);
  }, [safeConfig.requiredMatches, safeConfig.components.length]);

  const ratio = useMemo(() => {
    if (!requiredMatches) return 0;
    const matched = safeConfig.components.filter((c) => found.has(normalize(c.keyword || ''))).length;
    return Math.min(1, matched / requiredMatches);
  }, [found, requiredMatches, safeConfig.components]);

  useEffect(() => {
    if (ratio >= 1 && onComplete && !completedRef.current) {
      if (lessonId && !hasSavedRef.current) {
        const payload = {
          workspace: JSON.stringify({
            prompt,
            submittedPrompt,
            submitted: true,
          }),
        };
        try { saveUserAnswerById(lessonId, payload).catch(() => {/* ignore */}); } catch (_) {/* ignore */}
        hasSavedRef.current = true;
      }
      completedRef.current = true;
      onComplete();
    }
  }, [ratio, onComplete, lessonId, prompt, submittedPrompt, saveUserAnswerById]);

  const handleSubmit = () => {
    setSubmitted(true);
    setSubmittedPrompt(prompt);
    if (lessonId) {
      const payload = { workspace: JSON.stringify({ prompt, submittedPrompt: prompt, submitted: true }) };
      try { saveUserAnswerById(lessonId, payload).catch(() => {/* ignore */}); } catch (_) {/* ignore */}
    }
  };

  const canReveal = forceReveal || submitted;
  const matchedCount = safeConfig.components.filter((c) => found.has(normalize(c.keyword || ''))).length;
  const totalPieces = safeConfig.components.length;

  return (
    <div className="puzzle-student">
      <Card className="puzzle-main-card">
        <div className="puzzle-content-wrapper">
          {/* Statement Display */}
          {(statement || safeConfig.statement) && (
            <div className="puzzle-statement">
              {statement || safeConfig.statement}
            </div>
          )}
          {/* Mini Preview - Full Image */}
          {safeConfig.backgroundImage && (
            <div className="puzzle-mini-preview">
              <div className="puzzle-mini-preview-label">
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('puzzleLesson.student.previewLabel') || 'Aperçu final'}
                </Text>
              </div>
              <div className="puzzle-mini-preview-canvas">
                <img
                  src={safeConfig.backgroundImage}
                  alt="Puzzle preview"
                  className="puzzle-mini-background-image"
                />
                {safeConfig.components.map((component) => (
                  <div
                    key={`mini-${component.id}`}
                    className="puzzle-mini-piece"
                    style={{
                      left: `${component.x}%`,
                      top: `${component.y}%`,
                      width: `${component.width}%`,
                      height: `${component.height}%`,
                      backgroundImage: component.image ? `url(${component.image})` : undefined,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {/* Image Canvas - Top */}
          <div className="puzzle-canvas-wrapper">
            <div className="puzzle-canvas">
              {safeConfig.backgroundImage ? (
                <img
                  src={safeConfig.backgroundImage}
                  alt="Puzzle background"
                  className="puzzle-background-image"
                  onLoad={(e) => {
                    // Image loaded, it will determine its own height
                  }}
                />
              ) : (
                <div className="puzzle-canvas-empty">
                  <Text type="secondary">
                    {t('puzzleLesson.student.noBackground') || 'Aucune image configurée.'}
                  </Text>
                </div>
              )}
              {safeConfig.components.map((component) => {
                const keyword = normalize(component.keyword || '');
                const revealed = canReveal && found.has(keyword);
                return (
                  <div
                    key={component.id}
                    className={revealed ? 'puzzle-piece-student revealed' : 'puzzle-piece-student placeholder'}
                    style={{
                      left: `${component.x}%`,
                      top: `${component.y}%`,
                      width: `${component.width}%`,
                      height: `${component.height}%`,
                      opacity: revealed ? 1 : safeConfig.placeholderOpacity,
                      backgroundImage: component.image ? `url(${component.image})` : undefined,
                    }}
                  >
                    {!revealed && (
                      <div className="puzzle-piece-overlay">
                        <Text>{component.keyword}</Text>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {canReveal && matchedCount === 0 && (
            <Alert
              type="warning"
              showIcon
              className="puzzle-alert"
              message={t('puzzleLesson.student.noMatch') || "Aucun mot-clé trouvé. Réessaie avec d'autres mots-clés."}
            />
          )}
          {canReveal && matchedCount > 0 && matchedCount < requiredMatches && (
            <Alert
              type="info"
              showIcon
              className="puzzle-alert"
              message={t('puzzleLesson.student.keepTrying') || 'Continue, il reste des pièces à révéler !'}
            />
          )}
          {canReveal && ratio >= 1 && (
            <Alert
              type="success"
              showIcon
              className="puzzle-alert"
              message={t('puzzleLesson.student.success') || 'Bravo ! Toutes les pièces requises sont révélées.'}
              icon={<CheckCircleOutlined />}
            />
          )}

          {/* Prompt Input Section - Bottom */}
          <div className="puzzle-panel">
            <div className="puzzle-input-container">
              <TextArea
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('puzzleLesson.student.promptPlaceholder') || 'Écris ta prompt ici...'}
                disabled={forceReveal}
                className="puzzle-input-textarea"
                onPressEnter={(e) => {
                  if (!e.shiftKey && prompt.trim() && (!submitted || prompt !== submittedPrompt)) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <div className="puzzle-input-actions">
                {!forceReveal && (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSubmit}
                    disabled={!prompt.trim() || (submitted && prompt === submittedPrompt)}
                    className="puzzle-submit-button"
                    shape="circle"
                  />
                )}
              </div>
            </div>
            <div className="puzzle-progress-container">
              <Progress
                percent={requiredMatches ? Math.round(ratio * 100) : 0}
                status={ratio >= 1 ? 'success' : 'active'}
                showInfo={false}
                strokeWidth={6}
                className="puzzle-progress-bar"
              />
              <Text className="puzzle-progress-text">
                {matchedCount}/{requiredMatches || totalPieces} {t('puzzleLesson.student.progressTitle') || 'pièces'}
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PuzzleLessonStudent;
